import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useWidgetGridContext } from '../../context';
import { useWidgetData } from '../../hooks';
import { executeAction } from '../../utils';
import WidgetCardShell from '../WidgetCardShell';
import WidgetErrorBoundary from '../WidgetErrorBoundary';
import WidgetStatePlaceholder, { DEFAULT_STATE_COPY } from '../WidgetStatePlaceholder';
import WidgetSkeleton from '../WidgetSkeleton';
import Widget from '../Widget';
import ActivityListWidget from '../../customWidgets/ucpWidgets/ActivityListWidget';
import NotesListWidget from '../../customWidgets/ucpWidgets/NotesListWidget';
import { COMPRESSED_LAYOUT_W_THRESHOLD } from '../../customWidgets/ucpWidgets/ActivityListWidget/constants';
import { Container } from './WidgetWrapper.styled';

const CUSTOM_FIXED_REGISTRY = {
  activity: ActivityListWidget,
  notes: NotesListWidget,
};

const resolveCustomFixedKey = (widgetConfig) => {
  const defUuid = widgetConfig.widget_definition_uuid || '';
  if (defUuid.includes('notes')) return 'notes';
  if (defUuid.includes('activity')) return 'activity';
  return null;
};

const WidgetWrapperInner = ({ widgetConfig, layoutW }) => {
  const {
    scopeContext,
    dashboardUuid,
    activeFilters,
    onNavigate,
    onCreateNoteClick,
  } = useWidgetGridContext();

  const {
    data,
    loading,
    error,
    isEmpty,
    refetch,
    canRetry,
    containerRef,
  } = useWidgetData({
    instanceUuid: widgetConfig.instance_uuid,
    scopeContext,
    dashboardUuid,
    activeFilters,
    acceptsGlobalFilters: widgetConfig.accepts_global_filters,
  });

  const cardConfig = widgetConfig.config?.card || {};
  const title = cardConfig.title || '';
  const description = cardConfig.description || '';
  const headerButtons = cardConfig.header_buttons || [];
  const widgetType = widgetConfig.widget_type;

  const isCompressed = useMemo(
    () => layoutW != null && layoutW <= COMPRESSED_LAYOUT_W_THRESHOLD,
    [layoutW],
  );

  const customFixedKey = resolveCustomFixedKey(widgetConfig);

  const actionHandlers = useMemo(
    () => ({
      onNavigate,
      onRefresh: refetch,
      onSlideout: undefined,
      onPopup: undefined,
      onApiAction: undefined,
    }),
    [onNavigate, refetch],
  );

  const handleAction = useCallback(
    (action) => {
      executeAction(action, actionHandlers, { scopeContext });
    },
    [actionHandlers, scopeContext],
  );

  const footerSlot = useMemo(() => {
    const footerButtons = cardConfig.footer_buttons || [];
    if (footerButtons.length === 0) return null;
    return (
      <>
        {footerButtons.map((btn) => (
          <button key={btn.key} onClick={() => handleAction(btn.action)}>
            {btn.label}
          </button>
        ))}
      </>
    );
  }, [cardConfig.footer_buttons, handleAction]);

  const renderContent = () => {
    if (loading) return <WidgetSkeleton />;

    if (widgetType === 'CUSTOM_FIXED' && customFixedKey) {
      const WidgetComponent = CUSTOM_FIXED_REGISTRY[customFixedKey];
      if (WidgetComponent) {
        const baseProps = {
          data,
          isCompressed,
          error,
          errorCode: error?.code,
          onRetry: refetch,
          canRetry,
        };
        const extraProps =
          customFixedKey === 'notes'
            ? {
                onItemClick: (item) =>
                  handleAction({ type: 'NAVIGATE', target: `activity?noteId=${item.id}` }),
                onCreate: () => {
                  if (onCreateNoteClick) {
                    onCreateNoteClick();
                  } else {
                    handleAction({ type: 'NAVIGATE', target: 'activity' });
                  }
                },
              }
            : {};
        return <WidgetComponent {...baseProps} {...extraProps} />;
      }
    }

    if (error) {
      return (
        <WidgetStatePlaceholder
          error={error}
          errorCode={error?.code}
          stateCopy={DEFAULT_STATE_COPY}
          onRetry={refetch}
          canRetry={canRetry}
        />
      );
    }

    if (isEmpty) {
      return <WidgetStatePlaceholder isEmpty stateCopy={DEFAULT_STATE_COPY} />;
    }

    return <Widget data={data} />;
  };

  return (
    <Container ref={containerRef}>
      <WidgetErrorBoundary instanceId={widgetConfig.instance_uuid}>
        <WidgetCardShell
          title={title}
          description={description}
          headerButtons={headerButtons}
          onAction={handleAction}
          footerSlot={footerSlot}
        >
          {renderContent()}
        </WidgetCardShell>
      </WidgetErrorBoundary>
    </Container>
  );
};

WidgetWrapperInner.propTypes = {
  widgetConfig: PropTypes.shape({
    instance_uuid: PropTypes.string.isRequired,
    widget_type: PropTypes.string.isRequired,
    widget_definition_uuid: PropTypes.string,
    visualization_type: PropTypes.string,
    accepts_global_filters: PropTypes.bool,
    config: PropTypes.object,
  }).isRequired,
  layoutW: PropTypes.number,
};

WidgetWrapperInner.defaultProps = {
  layoutW: null,
};

const WidgetWrapper = memo(WidgetWrapperInner);

export default WidgetWrapper;
